import React, { useContext, useEffect, useState } from "react";
import Gallery from "../../../components/Gallery/Gallery";
import AttachmentApi from "../../../api/services/AttachmentApi";
import AttachmentFileHandler from "../../../helper/AttachmentFileHandler";
import FileReference from "../../../components/Gallery/model/FileReference.model";
import LoadedFile from "../../../components/Gallery/model/LoadedFile.model";
import Attachment from "../../../components/Gallery/model/Attachment.model";
import GalleryContext from "../../../contexts/GalleryContext";
import useEnv from "../../../hooks/env";
import TestRequestApi from "../../../api/services/TestRequestApi";
import ProductAttachment from "../../../api/models/product/ProductAttachment";
import ProductApi from "../../../api/services/ProductApi";

export interface ProductSampleGalleryProps {
  editable: boolean;
  testRequestId: string;
  productSampleId: number;
  productSampleName: string;
  productSampleAttachments: ProductAttachment[];
  onChange: (value: ProductAttachment[]) => void;
}

const ProductSampleGallery: React.FC<ProductSampleGalleryProps> = (props) => {
  const { editable, productSampleAttachments, onChange, productSampleId, productSampleName, testRequestId } = props;
  const { flags } = useContext(GalleryContext);
  const { api } = useEnv();
  const attachmentApi = new AttachmentApi(api);
  const testRequestApi = new TestRequestApi(api);
  const productSamplesApi = new ProductApi(api);
  const namingStrategy = {
    nameDetailDialog(file: LoadedFile) {
      return {
        header: `GALLERY ${productSampleName}`,
        subheader: file.name,
      };
    },
  };

  const mapAttachment = (value: ProductAttachment): Attachment => ({
    ...value,
    flag: flags?.find((f) => f.type === value.type),
  });
  const mapProductAttachment = (value: Attachment): ProductAttachment => ({
    ...value,
    type: value.flag?.type,
  });

  const attachmentFileHandler = new AttachmentFileHandler(
    attachmentApi,
    {
      remove: (idToBeDeleted) => {
        return productSamplesApi
          .removeAttachment(testRequestId, productSampleId, idToBeDeleted)
          .then((values) => values.map(mapAttachment))
          .then((attachments) => ({ attachments, idToBeDeleted }));
      },
      append: (ids, flag) =>
        productSamplesApi
          .addAttachment(testRequestId, productSampleId, ids)
          .then((values) => values.map(mapAttachment)),
      zipFileName(files: FileReference[]): string {
        if (files.length === 1) {
          const file = files[0];
          if (file.status === "LOADED") {
            return (file as LoadedFile).name;
          }
          return file.id;
        }
        return `LEAP-${testRequestId}-${productSampleName.replace(/[^A-Za-z0-9]/g, "_")}.zip`;
      },
    },
    () => testRequestApi.getTestRequestAttachmentClearanceToken(testRequestId).then((res) => res.data),
  );

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  useEffect(() => {
    setAttachments(productSampleAttachments?.map(mapAttachment));
  }, [productSampleAttachments]);

  return (
    <Gallery
      disabled={!editable}
      hideIfEmpty
      filterable
      galleryId={`ProductSample${productSampleId}`}
      fileHandler={attachmentFileHandler}
      namingStrategy={namingStrategy}
      attachments={attachments}
      onChange={(values) => onChange(values.map(mapProductAttachment))}
      uploadButtonLabel="Add Attachment"
      testRequestId={testRequestId}
    />
  );
};

export default ProductSampleGallery;
